/*
 * File: OverviewStep.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 04 Jan 2022 3:54:05 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Card, Col, Radio, Row, Space, Typography } from 'antd';
import { colors, toRgb } from 'common/styles/colors';
import { ProductsOverviewTable } from 'components/molecules/ProductsOverviewTable';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';

const styles: InlineStylesModel = {
  mainContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    margin: 0,
    width: '100%'
    // height: 'calc(100vh - 112px - 46px - 50px - 110px - 25px )'
  },
  cardsContainer: {
    padding: '0px',
    margin: '0px'
  },

  infoTitle: {
    fontSize: '16px',
    fontWeight: 600
  },
  infoText: {
    fontSize: '15px',
    minHeight: '27px'
  },
  cardHead: {
    fontSize: '18px',
    fontWeight: 600
  },
  cardBody: {
    padding: '25px 20px 20px 25px'
  },
  headerCard: {
    backgroundColor: '#FFEAC1'
  },
  deliveryCard: {
    backgroundColor: '#CED5F2'
  },
  productsTableCard: {
    backgroundColor: '#b0b2bc'
  },
  tableBody: {
    padding: 0
  },
  extra: {
    fontSize: '15px',
    fontWeight: 500
  },
  addressLine: {
    marginBottom: 0
  },
  attention: {
    maxWidth: '300px'
  },
  red: {
    color: toRgb(colors.redSalsa)
  }
};

export const OverviewStep = (): JSX.Element => {
  const {
    values: {
      attention,
      buyerName,
      deliveryAddressCity,
      deliveryAddressCountry,
      deliveryContactName,
      deliveryContactPhone,
      deliveryAddressDescription,
      freightTerms,
      labConfiguration,
      modeOfDelivery,
      quoteId,
      requestedShipDate,
      salesOrderType,
      salesPersonFullName,
      salesSupportFullName,
      shipComplete,
      siteId,
      deliveryAddressState,
      deliveryAddressStreet,
      warehouseId,
      deliveryAddressZipCode,
      lines
    }
  } = useFormikContext<SalesOrderPayloadModel>();

  /* ****************** Functions ****************** */
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });
  const numberFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal'
  });

  /* ****************** JSX  / Variables ****************** */
  const margin = lines.map((item) => Number(item.quantity) * Number(item.unitPrice) - Number(item.quantity) * Number(item.unitCost)).reduce((a, b) => a + b, 0);
  const totalQty = lines.map((item) => Number(item.quantity)).reduce((a, b) => a + b, 0);
  const totalPrice = lines.map((item) => Number(item.unitPrice) * Number(item.quantity)).reduce((a, b) => a + b, 0);

  const deliveryPhoneJSX = deliveryContactPhone ?? null;
  const deliveryContactJSX = deliveryContactName ?? null;

  const descriptionJSX = deliveryAddressDescription && <Typography.Paragraph style={{ ...styles.infoText, ...styles.addressLine }}>{deliveryAddressDescription}</Typography.Paragraph>;

  const streetJSX = deliveryAddressStreet && <Typography.Paragraph style={{ ...styles.infoText, ...styles.addressLine }}>{deliveryAddressStreet}</Typography.Paragraph>;
  const isInvalid = margin < 0;

  const formattedAddressJSX = (
    <Typography.Paragraph style={{ ...styles.infoText, ...styles.addressLine }}>
      {deliveryAddressCity}, {deliveryAddressState} {deliveryAddressZipCode}
      {deliveryAddressCountry}
    </Typography.Paragraph>
  );

  const headerInfoCardJSX = (
    <Card style={{ height: '100%', width: '100%' }} size="small" title="Header Info" bordered={false} headStyle={{ ...styles.cardHead, ...styles.headerCard }} bodyStyle={styles.cardBody}>
      <Row gutter={16}>
        <Col span={8}>
          <Typography.Title level={5} style={styles.infoTitle}>
            Salesperson:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{salesPersonFullName}</Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Sales Support:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{salesSupportFullName}</Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Buyer:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{buyerName}</Typography.Paragraph>
        </Col>
        <Col span={8}>
          <Typography.Title level={5} style={styles.infoTitle}>
            Sales Order Type:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{salesOrderType}</Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Site:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{siteId}</Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Warehouse:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{warehouseId}</Typography.Paragraph>
        </Col>
        <Col span={8}>
          <Typography.Title level={5} style={styles.infoTitle}>
            Quote Id:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{quoteId}</Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Is Lab Configuration Required
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>
            <Radio.Group disabled buttonStyle="solid" defaultValue={labConfiguration}>
              <Radio.Button value={true}>Yes</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
            </Radio.Group>
          </Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Is Ship Complete
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>
            <Radio.Group disabled buttonStyle="solid" defaultValue={shipComplete}>
              <Radio.Button value={true}>Yes</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
            </Radio.Group>
          </Typography.Paragraph>
        </Col>
      </Row>
    </Card>
  );

  const deliveryInfoCardJSX = (
    <Card style={{ height: '100%', width: '100%' }} size="small" title="Delivery Info" bordered={false} headStyle={{ ...styles.cardHead, ...styles.deliveryCard }} bodyStyle={styles.cardBody}>
      <Row gutter={[32, 0]}>
        <Col span={12}>
          <Typography.Title level={5} style={styles.infoTitle}>
            Delivery Contact:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>
            {deliveryContactJSX} {deliveryPhoneJSX}
          </Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Delivery Address:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>
            {descriptionJSX}
            {streetJSX}
            {formattedAddressJSX}
          </Typography.Paragraph>
        </Col>

        <Col span={6}>
          <Typography.Title level={5} style={styles.infoTitle}>
            Freight Terms:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{freightTerms}</Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Mode of Delivery:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{modeOfDelivery}</Typography.Paragraph>
        </Col>

        <Col span={6}>
          <Typography.Title level={5} style={styles.infoTitle}>
            Req. Ship Date:
          </Typography.Title>
          <Typography.Paragraph style={styles.infoText}>{new Date(requestedShipDate).toLocaleDateString()}</Typography.Paragraph>
          <Typography.Title level={5} style={styles.infoTitle}>
            Attention:
          </Typography.Title>
          <Typography.Paragraph style={{ ...styles.infoText, ...styles.attention }}>{attention}</Typography.Paragraph>
        </Col>
      </Row>
    </Card>
  );

  const lineItemsCardJSX = (
    <Card
      style={{ height: '100%' }}
      size="small"
      title="Line Items"
      bordered={false}
      headStyle={{ ...styles.cardHead, ...styles.productsTableCard }}
      bodyStyle={styles.tableBody}
      extra={
        <Space size="large">
          <Space>
            <Typography.Text style={styles.extra}>Total Qty: </Typography.Text>
            <Typography.Text style={styles.extra}>{totalQty && numberFormatter.format(totalQty)}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text style={styles.extra}>Total Price: </Typography.Text>
            <Typography.Text style={styles.extra}>{totalPrice ? currencyFormatter.format(totalPrice) : `$0.00`}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text style={styles.extra}>Margin: </Typography.Text>
            <Typography.Text style={isInvalid ? { ...styles.extra, ...styles.red } : { ...styles.extra }}>{currencyFormatter.format(margin || 0)}</Typography.Text>
          </Space>
        </Space>
      }>
      <ProductsOverviewTable />
    </Card>
  );

  /* ****************** Render ****************** */
  return (
    <Row style={styles.mainContainer} gutter={[0, 16]}>
      <Col span={24} style={styles.cardsContainer}>
        <Row gutter={[16, 16]}>
          <Col lg={24} xl={12} style={{ width: '100%' }}>
            {headerInfoCardJSX}
          </Col>
          <Col lg={24} xl={12} style={{ width: '100%' }}>
            {deliveryInfoCardJSX}
          </Col>
        </Row>
      </Col>
      <Col span={24} style={styles.cardsContainer}>
        <Row gutter={[0, 0]}>
          <Col span={24}>{lineItemsCardJSX}</Col>
        </Row>
      </Col>
    </Row>
  );
};
